import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { defaultRouteForUser } from "@/lib/routes";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user && user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  if (user && !user.must_change_password) {
    return <Navigate to={defaultRouteForUser(user)} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Welcome back");
    if (res.user.must_change_password) navigate("/change-password");
    else navigate(defaultRouteForUser(res.user));
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full flex items-center justify-center bg-[#F8FAFC] p-4"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-12 w-12 rounded-md bg-white border border-slate-200 flex items-center justify-center p-1">
              <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
            </div>
            <div className="text-left">
              <div className="text-xs uppercase tracking-widest text-slate-500">Welcome to</div>
              <div className="font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
                Bravura Packers Pvt Ltd
              </div>
            </div>
          </div>
        </div>

        <div
          data-testid="login-form-container"
          className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-7"
        >
          <div className="brand-accent-bar -mx-7 -mt-7 mb-6" data-testid="brand-accent-bar-login" />
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Chivo" }}>
            Sign in
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Access your HR & payroll workspace.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                placeholder="you@bravurapackers.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full brand-bg brand-bg-hover text-white font-medium"
            >
              {submitting ? "Signing in / Sign-in ho raha hai..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-xs brand-text hover:underline font-medium"
            >
              Forgot password? / Password bhool gaye?
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Trouble signing in? Contact your Super Admin.
        </p>
      </div>
    </div>
  );
}
